import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, DatePicker, Notification, Loading, Layout } from 'element-react';
import { loadCaptchaEnginge, LoadCanvasTemplate, validateCaptcha } from 'react-simple-captcha';
import { Auth, API } from 'aws-amplify';
import { listCompanys } from '../graphql/queries';
import { blobToBase64 } from '../utils/FormatUtils';
import { FILES_URL, IS_DEV, RFQ_API_URL } from '../utils/Constants';
import rfqStore from '../store/rfqStore';

function UploadProposalForm({ rfq, onProposalSubmit }) {
    const [loading, setLoading] = useState(false);
    const [formInputs, setFormInputs] = useState({
        quotationFile: null,
        fileName: '',
        totalCost: '',
        validThrough: null,
        captcha: ''
    });
    const formRef = useRef(null);

    useEffect(() => {
        loadCaptchaEnginge(6);
    }, []);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormInputs(prev => ({ ...prev, quotationFile: file, fileName: file.name }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const isFormValid = await new Promise(resolve => formRef.current.validate(valid => resolve(valid)));
        if (!isFormValid) return;

        if (validateCaptcha(formInputs.captcha, false) === false) {
            Notification.error('Captcha does not match. Please try again.');
            loadCaptchaEnginge(6);
            setFormInputs(prev => ({ ...prev, captcha: '' }));
            return;
        }

        setLoading(true);

        if (IS_DEV) {
            // --- MODO DESARROLLO (IS_DEV = true) ---
            console.log("MODO DESARROLLO: Guardando propuesta localmente.");
            
            const { addProposal } = rfqStore.getState();

            const mockProposal = {
                rfq_id: rfq.id,
                total_cost: formInputs.totalCost,
                valid_date: formInputs.validThrough,
                file_key: `mock/path/${formInputs.fileName}`,
                id: new Date().getTime(),
                creation_date: new Date().toISOString(),
            };

            addProposal(mockProposal); // Guardamos la propuesta en el store

            setTimeout(() => {
                Notification.success('Mock proposal submitted locally!');
                setLoading(false);
                if (onProposalSubmit) onProposalSubmit();
            }, 500);

        }
        else{
            try {
                const user = await Auth.currentAuthenticatedUser();
                const companyData = await API.graphql({
                    query: listCompanys,
                    variables: { filter: { owner: { eq: user.username } } }
                });
                const company = companyData.data.listCompanys.items[0];

                if (!company) {
                    throw new Error("Could not find company information for the current user.");
                }

                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                let s3FileKey = null;

                if (formInputs.quotationFile) {
                    s3FileKey = `/proposals/${user.username}/${Date.now()}_${formInputs.fileName}`;
                    const base64Data = await blobToBase64(formInputs.quotationFile);
                    const base64Content = base64Data.split(',')[1];
                    
                    await fetch(FILES_URL, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': token },
                        body: JSON.stringify({
                            fileName: s3FileKey,
                            fileType: formInputs.quotationFile.type,
                            file: base64Content
                        })
                    });
                }

                const proposalDetails = {
                    companyName: company.name,
                    companyId: company.id,
                    totalCost: parseFloat(formInputs.totalCost),
                    validDate: formInputs.validThrough.toISOString(),
                    fileBucket: 'marketplace19950c81227542e0b3222b702ca325c8141408-dev',
                    fileKey: s3FileKey,
                    fileRegion: 'us-east-2'
                };

                const response = await fetch(`${RFQ_API_URL}/createPropousal/${rfq.id}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': token },
                    body: JSON.stringify({ proposalDetails })
                });

                if (!response.ok) throw new Error('Failed to submit proposal');

                const data = await response.json();
                
                if (data.returnCode === 0) {
                    Notification.success('Proposal submitted successfully!');
                    if (onProposalSubmit) {
                        onProposalSubmit(data.proposalId);
                    }
                } else {
                    throw new Error(data.returnMessage || 'Failed to create proposal');
                }

            } catch (error) {
                console.error("Error submitting proposal:", error);
                Notification.error("Failed to submit proposal. " + error.message);
            } finally {
                setLoading(false);
            }
        }
       
    };

    return (
        <div className="upload-proposal-form upload-proposal-form-responsive">
            {loading && <Loading fullscreen={true} />}
            <h3>Upload Quotation</h3>
            
            <div className="rfq-info">
                <span>{rfq.id}</span>
                <span className="product-name-highlight">{rfq.product_name}</span>
            </div>

            <Form ref={formRef} model={formInputs} labelPosition="top" onSubmit={handleSubmit}>
                <Form.Item label="Upload quotation file:" prop="quotationFile" required={true}>
                    <div className="file-input-group">
                        <Input value={formInputs.fileName} readOnly={true} placeholder="No file selected" />
                        <label className="el-button el-button--primary">
                            Browse
                            <input type="file" style={{display: 'none'}} onChange={handleFileChange} />
                        </label>
                    </div>
                </Form.Item>

                <Form.Item label="What's the Total in the Quotation?" prop="totalCost" required={true}>
                    <Input type="number" value={formInputs.totalCost} onChange={(value) => setFormInputs(prev => ({...prev, totalCost: value}))} />
                </Form.Item>

                <Form.Item label="Valid Through" prop="validThrough" required={true}>
                    <DatePicker
                        value={formInputs.validThrough}
                        placeholder="Pick a Date"
                        onChange={(date) => setFormInputs(prev => ({...prev, validThrough: date}))}
                    />
                </Form.Item>

                <Layout.Row className='div-Captchat'>
                    <Layout.Col xs={8} xm={12} lg={12}>
                        <Form.Item label="Captcha">
                            <LoadCanvasTemplate reloadText="Reload Captcha" />
                        </Form.Item>
                    </Layout.Col>
                    <Layout.Col xs={14} xm={12} lg={12}>
                        <Form.Item label="Please enter the Captcha text" prop="captcha" required={true}>
                            <Input value={formInputs.captcha} onChange={(value) => setFormInputs(prev => ({...prev, captcha: value}))} />
                        </Form.Item>
                    </Layout.Col>
                </Layout.Row>

                <Form.Item>
                    <Button className="upload-button" type="primary" nativeType="submit">Upload Proposal</Button>
                </Form.Item>
            </Form>
        </div>
    );
}

export default UploadProposalForm;